import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { loadConfig, getAmplifyConfig, AppConfig } from './config';

export interface UserInfo {
  displayName: string;
  email: string | null;
  orgId?: string;
  orgName?: string;
  ssoOnly?: boolean;
}

// SSO Discovery response
export interface SSODiscoveryResult {
  ssoEnabled: boolean;
  ssoEnforced?: boolean;
  organizationName?: string;
  organizationSlug?: string;
  ssoLoginUrl?: string;
  message: string;
}

interface AuthContextType {
  user: AuthUser | null;
  userInfo: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  config: AppConfig | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithSSO: (orgSlug: string, returnTo?: string) => void;
  discoverSSO: (email: string) => Promise<SSODiscoveryResult>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);

  // Fetch user display info (name, email, org) from Cognito attributes
  const fetchUserInfo = useCallback(async (authUser: AuthUser): Promise<UserInfo> => {
    try {
      const attributes = await fetchUserAttributes();
      return {
        displayName: attributes.name || attributes.email || authUser.username || 'User',
        email: attributes.email || null,
        orgId: attributes['custom:orgId'] || undefined,
        ssoOnly: attributes['custom:ssoOnly'] === 'true',
      };
    } catch (error) {
      console.warn('Failed to fetch user attributes:', error);
      return { displayName: authUser.username || 'User', email: null };
    }
  }, []);

  // Initialize Amplify configuration
  useEffect(() => {
    async function initConfig() {
      try {
        const appConfig = await loadConfig();
        setConfig(appConfig);

        const amplifyConfig = getAmplifyConfig(appConfig);
        Amplify.configure(amplifyConfig);
        setIsConfigured(true);
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setIsLoading(false);
      }
    }

    initConfig();
  }, []);

  // Check current user after configuration
  useEffect(() => {
    if (!isConfigured) return;

    async function checkUser() {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        const info = await fetchUserInfo(currentUser);
        setUserInfo(info);
      } catch {
        setUser(null);
        setUserInfo(null);
      } finally {
        setIsLoading(false);
      }
    }

    checkUser();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          getCurrentUser()
            .then(async (currentUser) => {
              setUser(currentUser);
              const info = await fetchUserInfo(currentUser);
              setUserInfo(info);
            })
            .catch(() => {
              setUser(null);
              setUserInfo(null);
            });
          break;
        case 'signedOut':
          setUser(null);
          setUserInfo(null);
          break;
      }
    });

    return () => unsubscribe();
  }, [isConfigured]);

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    try {
      await signIn({ username: email, password });
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Email sign in error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      setUserInfo(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      // Use ID token as it contains the 'aud' claim needed by API Gateway JWT authorizer
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  // Discover SSO for an email address
  const discoverSSO = useCallback(async (email: string): Promise<SSODiscoveryResult> => {
    if (!config?.api?.endpoint) {
      return { ssoEnabled: false, message: 'API not configured' };
    }

    try {
      const response = await fetch(
        `${config.api.endpoint}/auth/sso/discover?email=${encodeURIComponent(email)}`
      );

      if (!response.ok) {
        return { ssoEnabled: false, message: 'Failed to check SSO status' };
      }

      const data = await response.json();
      return data.data as SSODiscoveryResult;
    } catch (error) {
      console.error('SSO discovery error:', error);
      return { ssoEnabled: false, message: 'Error checking SSO status' };
    }
  }, [config]);

  // Redirect to SSO login
  const signInWithSSO = useCallback((orgSlug: string, returnTo?: string) => {
    if (!config?.api?.endpoint) {
      console.error('API not configured for SSO');
      return;
    }

    const callbackUrl = `${window.location.origin}/auth/callback`;
    const ssoUrl = new URL(`${config.api.endpoint}/auth/sso/${orgSlug}`);
    ssoUrl.searchParams.set('callback_url', callbackUrl);
    if (returnTo) {
      ssoUrl.searchParams.set('returnTo', returnTo);
    }

    // Redirect to SSO endpoint which will redirect to the IdP
    window.location.href = ssoUrl.toString();
  }, [config]);

  const value: AuthContextType = {
    user,
    userInfo,
    isLoading,
    isAuthenticated: !!user,
    config,
    signInWithGoogle,
    signInWithEmail,
    signInWithSSO,
    discoverSSO,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
